import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import ReactPlayer from 'react-player';

import HeadingCtas from '../../Generic/HeadingCtas';
import ContentfulImage from '../ContentfulImage';
import { ArrowLink } from '../Links';
import * as inlines from './Inlines';
import * as styles from './RichTextConverter.module.scss';
import YouTubeVideo from './YouTubeVideo';

/* eslint-disable */
export const defaultOptions = {
  renderText: inlines.text,
  renderNode: {
    [INLINES.HYPERLINK]: inlines.hyperlink,
    [INLINES.EMBEDDED_ENTRY]: inlines.embeddedEntry,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { file, title, description, gatsbyImageData, height, width } = node?.data?.target || {};

      return (
        <figure className={styles.figure} key={file.url}>
          {gatsbyImageData && (
            <GatsbyImage
              data-ot-ignore
              className={styles.embeddedAsset}
              image={gatsbyImageData}
              alt={title}
              loading={'lazy'}
            />
          )}
          {!gatsbyImageData && <img src={file?.url} alt={title} loading={'lazy'} height={height} width={width} />}
          {description && <figcaption>{description}</figcaption>}
        </figure>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const type = node?.data?.target?.internal?.type;

      if (type === 'ContentfulComponentTestimonial') {
        const attributionImage = node?.data?.target?.attributionImage;
        const attribution = node?.data?.target?.attribution;
        const company = node?.data?.target?.company;
        const quote = node?.data?.target?.quote?.quote;
        return (
          <div className={styles.quote} key={`${company}-${attribution}`}>
            {attributionImage && (
              <ContentfulImage className={styles.attributionImage} imageFragment={attributionImage} lazy={true} />
            )}
            <p>{quote}</p>
            <span>{attribution}</span>
            <span>{company}</span>
          </div>
        );
      }

      // Anchor
      if (type === 'ContentfulEmbedJumpLink') {
        let id = node?.data?.target?.sectionId;
        return <div id={id} key={id} />;
      }

      if (type === 'ContentfulEmbedLink') {
        const isArrowLink = node.data.target.linkStyle === 'Arrow Link';
        const label = node?.data?.target?.linkText;
        const url = node?.data?.target?.url;
        return isArrowLink ? (
          <ArrowLink url={url} label={label} key={url} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }} key={url}>
            <HeadingCtas ctas={[node.data.target]} />
          </div>
        );
      }

      // for wistia embeds
      if (type === 'ContentfulEmbedVideo') {
        const videoUrl = node.data.target.videoUrl;
        if (videoUrl?.indexOf('wistia') > -1) {
          return (
            <div className={styles.playerWrapper} key={videoUrl}>
              <HydrationProvider>
                <Client>
                  <ReactPlayer
                    className={[styles.reactPlayer, `react-player-${videoUrl}`].join(' ')}
                    controls={true}
                    width="100%"
                    height="100%"
                    src={videoUrl}
                    url={videoUrl}
                  />
                </Client>
              </HydrationProvider>
            </div>
          );
        }

        return <YouTubeVideo videoUrl={videoUrl} key={videoUrl} />;
      }

      // embedded images for alignment
      if (type === 'ContentfulEmbedInlineImage') {
        const image = node?.data?.target?.image;
        if (!image) return;
        const side = node?.data?.target?.alignImage;

        let align = '';
        switch (side) {
          case 'Left':
            align = styles.left;
            break;
          case 'Right':
            align = styles.right;
            break;
          default:
            align = styles.fullWidth;
        }

        if (!image.fullWidth) {
          let img = <img loading={'lazy'} src={image.file.url} alt={image.title} />;
          if (node?.data?.target?.url) {
            img = (
              <a href={node?.data?.target?.url} target="_blank" rel="noreferrer">
                {img}
              </a>
            );
          }
          return (
            <div className={align} key={image.file.url}>
              {img}
            </div>
          );
        }

        let img = <GatsbyImage loading={'lazy'} image={image.fullWidth} alt={image.title} />;
        if (node?.data?.target?.url) {
          img = (
            <a href={node?.data?.target?.url} target="_blank" rel="noreferrer">
              {img}
            </a>
          );
        }

        return (
          <div className={align} key={image.file.url}>
            {img}
          </div>
        );
      }
    },
  },
};

const RichTextConverter = (props) => {
  // Either or rather than merge, as merge isn't working in this situation.
  const option = props.options || defaultOptions;
  return <div className={props.className || ''}>{props.copy?.raw && renderRichText(props.copy, option)}</div>;
};

RichTextConverter.propTypes = {
  copy: PropTypes.object,
  options: PropTypes.object,
  className: PropTypes.string,
};

export default RichTextConverter;
